import { resolveMenuGridColumns, type EditTarget } from '@web-studio/shared-schemas';
import { EditableContent } from '../../../edit/EditableContent';
import { EditableMedia } from '../../../edit/EditableMedia';
import { buildSiteImageProxyUrl, buildSiteImageSrcSet } from '../../../lib/imageProxy';
import { SITE_IMAGE_SIZES } from '../../../lib/responsiveImageSizes';
import { pickBlockField } from '../../../lib/localized';
import { pickMediaUrl } from '../../../lib/media';
import { parseMenuColumns, type MenuColumn } from '../../../lib/parseMenuColumns';
import type { BlockRenderProps } from '../../../types';
import { MenuItemDietaryTags, MenuItemName, MenuItemOrderCode, MenuItemPriceSlot } from '../MenuItemTags';
import { MenuLegalLegend } from '../MenuLegalLegend';
import {
  isMenuColumnActive,
  isMenuItemActive,
  menuItemHasContent,
} from '../menuEditHelpers';


function MenuColumnView({
  column,
  columnIndex,
  editable,
  activeEditTarget,
  locale,
}: {
  column: MenuColumn;
  columnIndex: number;
  editable?: boolean;
  activeEditTarget?: EditTarget | null;
  locale: string;
}) {
  const columnTitle = column.title.trim();
  const columnActive = isMenuColumnActive(activeEditTarget, columnIndex);

  return (
    <div>
      {columnTitle ? (
        <h3
          className={['ws-menu__column-title', columnActive ? 'ws-edit-child--active' : '']
            .filter(Boolean)
            .join(' ')}
          {...(editable
            ? {
                'data-edit-child': 'menu-column' as const,
                'data-column-index': columnIndex,
              }
            : {})}
        >
          {column.title}
        </h3>
      ) : null}
      {column.items.map((item, itemIndex) => {
        if (!menuItemHasContent(item)) {
          return null;
        }
        const itemActive = isMenuItemActive(
          activeEditTarget?.kind === 'menu-item' ? activeEditTarget : null,
          columnIndex,
          itemIndex,
        );
        return (
          <article
            key={`${item.name}-${itemIndex}`}
            className={['ws-menu__item', itemActive ? 'ws-edit-child--active' : '']
              .filter(Boolean)
              .join(' ')}
            {...(editable
              ? {
                  'data-edit-child': 'menu-item' as const,
                  'data-column-index': columnIndex,
                  'data-item-index': itemIndex,
                }
              : {})}
          >
            <div className="ws-menu__item-row">
              <MenuItemOrderCode code={item.code} />
              <div className="ws-menu__item-copy">
                <div className="ws-menu__item-primary">
                  <MenuItemName item={item} locale={locale} showOrderCode={false} />
                  <MenuItemDietaryTags
                    item={item}
                    locale={locale}
                    iconsOnly
                    placement="inline"
                  />
                </div>
                {item.description ? <p className="ws-menu__item-desc">{item.description}</p> : null}
              </div>
              <MenuItemPriceSlot price={item.price} />
            </div>
          </article>
        );
      })}
    </div>
  );
}

function MenuIntro({
  sectionTitle,
  sectionSubtitle,
  editable,
  target,
}: {
  sectionTitle: string;
  sectionSubtitle: string;
  editable?: boolean;
  target: EditTarget | null;
}) {
  return (
    <div className="ws-menu__intro">
      <EditableContent
        as="h2"
        fieldKey="sectionTitle"
        editable={editable}
        activeEditTarget={target}
        className="ws-menu__title"
      >
        {sectionTitle}
      </EditableContent>
      <EditableContent
        fieldKey="sectionSubtitle"
        editable={editable}
        activeEditTarget={target}
        className="ws-menu__subtitle"
      >
        {sectionSubtitle}
      </EditableContent>
    </div>
  );
}

/**
 * Classic 3-column Speisekarte. With `menuImage`, title rides a taller photo
 * masthead (one opening plane) instead of a short mid-page banner strip.
 */
export function MenuGrid3Column({
  block,
  locale,
  isActive,
  activeEditTarget,
  editable,
}: BlockRenderProps) {
  const sectionTitle = pickBlockField(block.content, 'sectionTitle', locale);
  const sectionSubtitle = pickBlockField(block.content, 'sectionSubtitle', locale);
  const menuDataRaw = pickBlockField(block.content, 'menuData', locale);
  const menuImage = pickMediaUrl(block, 'menuImage');
  const menuImageSrc = menuImage ? buildSiteImageProxyUrl(menuImage, 1600, 78) : undefined;
  const menuImageSrcSet = menuImage
    ? buildSiteImageSrcSet(menuImage, [480, 768, 1200, 1600, 2000], 78)
    : undefined;
  const columns = parseMenuColumns(menuDataRaw);
  const menuColumns = resolveMenuGridColumns(block.content);
  const target = isActive ? (activeEditTarget ?? null) : null;
  const hasMasthead = Boolean(menuImage);

  return (
    <section
      className={[
        'ws-menu',
        hasMasthead ? 'ws-menu--masthead' : '',
        isActive ? 'ws-block--active' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      data-block-id={block.blockId}
      data-component={block.componentType}
      data-menu-columns={menuColumns}
      id="menu"
    >
      {hasMasthead ? (
        <div className="ws-menu__masthead">
          <div className="ws-menu__visual">
            <EditableMedia
              fieldKey="menuImage"
              editable={editable}
              activeEditTarget={target}
              className="ws-menu__visual-frame"
              src={menuImageSrc}
              srcSet={menuImageSrcSet}
              sizes={SITE_IMAGE_SIZES.menuBanner}
              alt={sectionTitle || 'Menu'}
              width={1600}
              height={900}
              loading="eager"
              fetchPriority="high"
              decoding="async"
            />
            <div className="ws-menu__masthead-veil" aria-hidden />
          </div>
          <MenuIntro
            sectionTitle={sectionTitle}
            sectionSubtitle={sectionSubtitle}
            editable={editable}
            target={target}
          />
        </div>
      ) : (
        <MenuIntro
          sectionTitle={sectionTitle}
          sectionSubtitle={sectionSubtitle}
          editable={editable}
          target={target}
        />
      )}
      <div className="ws-menu__body">
        {columns.length > 0 ? (
          <div className="ws-menu__grid">
            {columns.map((column, columnIndex) => (
              <MenuColumnView
                key={`${column.title}-${columnIndex}`}
                column={column}
                columnIndex={columnIndex}
                editable={editable}
                activeEditTarget={target}
                locale={locale}
              />
            ))}
          </div>
        ) : null}
        <MenuLegalLegend columns={columns} locale={locale} />
      </div>
    </section>
  );
}
